import React from 'react';
import {
  func,
  bool,
  string,
  shape,
} from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import TextBox from '../form/TextBox';
import EmailTextBox from '../form/EmailTextBox';
import Password from '../form/PasswordContainer';
import validate from './validate';
import asyncValidate from './asyncValidate';
import { required, validEmail, validPassword } from '../form/validators';
import RegisterConfirmation from './RegisterConfirmationContainer';

export const RegistrationForm = (props) => {
  const {
    handleSubmit,
    serverMessage,
    signIn,
    isSubmitting,
    prefillLoginEmail,
    updateIsUniqueEmail,
    isUniqueEmail,
    initialValues,
    showAlreadyHaveAccount,
    finishCreatingAccount,
    showSignIn,
    showAccountBenefitsText,
  } = props;

  const disabled = isSubmitting ? { disabled: 'disabled' } : {};
  return (
    <form onSubmit={handleSubmit}>
      <div className="cf">
        <div className="fl w-100 ph2">
          <Field
            name="firstName"
            label={!finishCreatingAccount ? 'First Name' : ''}
            dataTestId="my-account-register-firstName"
            component={TextBox}
            validate={[required]}
            type={finishCreatingAccount ? 'hidden' : 'text'}
          />
        </div>
        <div className="fl w-100 ph2">
          <Field
            name="lastName"
            label={!finishCreatingAccount ? 'Last Name' : ''}
            dataTestId="my-account-register-lastName"
            component={TextBox}
            validate={[required]}
            type={finishCreatingAccount ? 'hidden' : 'text'}
          />
        </div>
        {finishCreatingAccount && (
          <div className="f6 ph2 mb2">
            <span className="dark-gray">Email: </span>
            <span>{initialValues.email}</span>
          </div>
        )}
        <div className="fl w-100 ph2">
          <Field
            name="email"
            label={!finishCreatingAccount ? 'Enter your email' : ''}
            updateIsUniqueEmail={updateIsUniqueEmail}
            signIn={signIn}
            prefillLoginEmail={prefillLoginEmail}
            isUniqueEmail={isUniqueEmail}
            dataTestId="my-account-register-email"
            component={EmailTextBox}
            validate={[required, validEmail]}
            type={finishCreatingAccount ? 'hidden' : 'text'}
            readOnly={initialValues && !!initialValues.email}
            showSignIn={showSignIn}
          />
        </div>
        <div className="fl w-100 ph2">
          <div className="relative gcc-hide-child-ns">
            <Field
              name="password"
              label="Password"
              dataTestId="my-account-register-password"
              component={Password}
              validate={[required, validPassword]}
              type="password"
              selectorName="register"
              showRequirements
            />
          </div>
        </div>
      </div>
      <div className="fl w-100 ph2">
        <div className="flex items-center mt2 mb3">
          <span style={{ color: 'red' }}>
            {serverMessage}
          </span>
        </div>
      </div>
      <button
        type="submit"
        data-test-id="my-account-register-button"
        className="gcc-btn gcc-btn-primary flex justify-center items-center w-100 pa3 bw0 white mv3 f4"
        {...disabled}
      >
        {
          isSubmitting &&
          <div className="relative">
            <div className="gcc-loading h2 w2 nl5 nt3 absolute left-1">
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
              <div className="w-100 h-100 absolute left-0 top-0" />
            </div>
          </div>
        }
        <div>Create My Account</div>
      </button>
      {showAccountBenefitsText && (
        <ul className="f6 mb0 ml3">
          <li>Track your order</li>
          <li>Change delivery information (within 24 hours of ordering)</li>
          <li>View past order details</li>
          <li>Save carts and favorite products</li>
        </ul>
      )}
      {showAlreadyHaveAccount && (
        <div className="fl f6 w-100 ph2 tc">
          <p>Already have an account? <span data-test-id="my-account-login-text-link" role="button" tabIndex={-3} className="blue pointer" onClick={signIn}>Sign In</span>.</p>
        </div>
      )}
    </form>
  );
};

RegistrationForm.defaultProps = {
  initialValues: {},
  finishCreatingAccount: false,
  showSignIn: true,
  showAccountBenefitsText: false,
};

RegistrationForm.propTypes = {
  handleSubmit: func.isRequired,
  isSubmitting: bool.isRequired,
  serverMessage: string.isRequired,
  signIn: func.isRequired,
  prefillLoginEmail: func.isRequired,
  updateIsUniqueEmail: func.isRequired,
  isUniqueEmail: bool.isRequired,
  initialValues: shape({
    firstName: string,
    lastName: string,
    email: string,
    accountInvitationGuid: string,
  }),
  showAlreadyHaveAccount: bool.isRequired,
  finishCreatingAccount: bool,
  showSignIn: bool,
  showAccountBenefitsText: bool,
};

const inviteToRegister = hasSuccessfullyCreatedAccount => (!hasSuccessfullyCreatedAccount);

const RegisterComponent = (props) => {
  const {
    isSubmitting,
    handleSubmit,
    serverMessage,
    signIn,
    prefillLoginEmail,
    updateIsUniqueEmail,
    isUniqueEmail,
    hasSuccessfullyCreatedAccount,
    initialValues,
    showAlreadyHaveAccount,
    finishCreatingAccount,
    showSignIn,
    showAccountBenefitsText,
  } = props;

  const registerFormProps = {
    handleSubmit,
    isSubmitting,
    prefillLoginEmail,
    serverMessage,
    signIn,
    updateIsUniqueEmail,
    isUniqueEmail,
    initialValues,
    showAlreadyHaveAccount,
    finishCreatingAccount,
    showSignIn,
    showAccountBenefitsText,
  };

  const registrationElement =
    inviteToRegister(hasSuccessfullyCreatedAccount) ?
      <RegistrationForm {...registerFormProps} /> :
      <RegisterConfirmation />;
  return (
    <div className="mh1 mh2-m mh3-l mv3">
      {registrationElement}
    </div>
  );
};

RegisterComponent.defaultProps = {
  initialValues: {},
  finishCreatingAccount: false,
  showSignIn: true,
  showAccountBenefitsText: true,
};

RegisterComponent.propTypes = {
  handleSubmit: func.isRequired,
  isSubmitting: bool.isRequired,
  signIn: func.isRequired,
  prefillLoginEmail: func.isRequired,
  updateIsUniqueEmail: func.isRequired,
  isUniqueEmail: bool.isRequired,
  serverMessage: string.isRequired,
  hasSuccessfullyCreatedAccount: bool.isRequired,
  initialValues: shape({
    firstName: string,
    lastName: string,
    email: string,
    accountInvitationGuid: string,
  }),
  showAlreadyHaveAccount: bool.isRequired,
  finishCreatingAccount: bool,
  showSignIn: bool,
  showAccountBenefitsText: bool,
};

export default reduxForm({
  form: 'register',
  validate,
  asyncValidate,
  asyncBlurFields: ['email'],
})(RegisterComponent);
